import React from 'react';
import {
  Box,
  Typography,
  Slider,
  TextField,
  InputAdornment,
  useTheme
} from '@mui/material';

const SliderQuestion = ({ 
  question, 
  value = question.min || 0, 
  onChange, 
  interpolateText, 
  answers, 
  error 
}) => {
  const theme = useTheme();

  const handleSliderChange = (event, newValue) => {
    onChange(newValue);
  };

  const handleInputChange = (event) => {
    const newValue = parseInt(event.target.value, 10);
    if (!isNaN(newValue)) {
      onChange(Math.min(Math.max(newValue, question.min), question.max));
    }
  };

  // Generate meaningful marks for the slider
  const getSliderMarks = () => {
    const marks = [];
    const range = question.max - question.min;
    const step = question.step || 1;
    
    // Add marks at key positions
    marks.push({ value: question.min, label: String(question.min) });
    
    if (range <= 20) {
      // For small ranges, show more marks
      for (let i = question.min + step; i < question.max; i += step * 2) {
        marks.push({ value: i, label: String(i) });
      }
    } else {
      // For larger ranges, show fewer marks
      const numMarks = 4;
      for (let i = 1; i < numMarks; i++) {
        const markValue = Math.round(question.min + (range * i / numMarks));
        marks.push({ value: markValue, label: String(markValue) });
      }
    }
    
    marks.push({ value: question.max, label: String(question.max) });
    return marks;
  };

  const formatDisplayValue = (val) => {
    if (question.unit) {
      return `${val.toLocaleString()} ${question.unit}`;
    }
    return val.toLocaleString();
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        {interpolateText(question.text, answers)}
      </Typography>
      
      {/* Current value display */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" color="primary.main" sx={{ fontWeight: 600 }}>
          {formatDisplayValue(value)}
        </Typography>
      </Box>

      {/* Slider */}
      <Box sx={{ px: 2, mb: 4 }}>
        <Slider
          value={value}
          onChange={handleSliderChange}
          min={question.min}
          max={question.max}
          step={question.step || 1}
          marks={getSliderMarks()}
          valueLabelDisplay="auto"
          valueLabelFormat={formatDisplayValue}
          sx={{
            height: 8,
            '& .MuiSlider-track': {
              backgroundColor: theme.palette.primary.main,
              border: 'none'
            },
            '& .MuiSlider-thumb': {
              height: 24,
              width: 24,
              backgroundColor: theme.palette.primary.main,
              border: '2px solid currentColor',
              '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                boxShadow: 'inherit'
              },
              '&:before': {
                display: 'none'
              }
            },
            '& .MuiSlider-valueLabel': {
              lineHeight: 1.2,
              fontSize: 12,
              background: 'unset',
              padding: 0,
              width: 32,
              height: 32,
              borderRadius: '50% 50% 50% 0',
              backgroundColor: theme.palette.primary.main,
              transformOrigin: 'bottom left',
              transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
              '&:before': { display: 'none' },
              '&.MuiSlider-valueLabelOpen': {
                transform: 'translate(50%, -100%) rotate(-45deg) scale(1)'
              },
              '& > *': {
                transform: 'rotate(45deg)'
              }
            }
          }}
        />
      </Box>

      {/* Manual input */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <TextField
          type="number"
          value={value}
          onChange={handleInputChange}
          inputProps={{
            min: question.min,
            max: question.max,
            step: question.step || 1
          }}
          InputProps={{
            endAdornment: question.unit ? (
              <InputAdornment position="end">{question.unit}</InputAdornment>
            ) : null
          }}
          variant="outlined"
          size="small"
          sx={{ width: 150 }}
          label="Enter manually"
        />
      </Box>
      
      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default SliderQuestion;